import { z } from "zod";

/**
 * Nested-tree input shape for creating JSXElement / JSXTextElement canvas
 * elements from an external source (e.g. an MCP client that has translated
 * a JSX/TSX file into this shape).
 *
 * Relationship to the canvas types in `elements.ts`:
 *   - `jsxType` matches `JSXElement.jsxType`. Accepted values:
 *       * HTML tag names (`div`, `span`, `button`, `img`, `input`, …)
 *       * Inline SVG primitives (`svg`, `g`, `path`, `circle`, `rect`,
 *         `line`, `polygon`, `polyline`, `defs`, `linearGradient`, `stop`,
 *         `mask`, `clipPath`, `use`) — use these to inline custom
 *         `.svg?react` assets that aren't in lucide.
 *       * shadcn component names (`Button`, `Card`, `Input`, …)
 *       * lucide icon names (`ChevronRight`, `Search`, `X`, …)
 *       * recharts component names (`LineChart`, `XAxis`, …)
 *   - `props` matches `JSXElement.props`. The server parses `className` into
 *     `styles` / `layout` during normalisation; everything else (including
 *     SVG attributes like `viewBox`, `d`, `fill`, `stroke`, and inline
 *     `style` objects) is passed through to the renderer. Callers should
 *     express sizing, spacing, color, and layout via `className` — inline
 *     `style` props bypass the parse and make elements second-class in the
 *     property panel, layout engine, and code-export round-trip. Reserve
 *     `style` for things Tailwind literally can't express (layered
 *     gradients, `backgroundSize`/`backgroundPosition` combos).
 *   - `children` is nested — the server assigns deterministic ids, computes
 *     `parentId`, and flattens the tree into the canvas's id→element record.
 *   - `id`, `parentId`, `x`, `y`, `width`, `height`, `styles`, `layout` are
 *     intentionally absent — they are derived. Callers shouldn't produce them.
 *
 * Text children may be plain strings (concise, idiomatic JSX) or explicit
 * `JSXTextElementInput` objects when the caller wants to label the text node
 * for the editor's layer panel.
 *
 * Authoring guide for MCP clients (icons, styling tokens, multi-branch
 * components) lives in the `import-jsx` tool description in
 * `apps/api/src/rest-router/mcp/mcp.router.ts`.
 */

export const JSXTextElementInput = z
  .object({
    text: z.string(),
  })
  .strict();
export type JSXTextElementInput = z.infer<typeof JSXTextElementInput>;

const BaseJSXElementInput = z.object({
  jsxType: z.string(),
  props: z.record(z.string(), z.unknown()).optional(),
});
type BaseJSXElementInput = z.infer<typeof BaseJSXElementInput>;

export const JSXElementInput: z.ZodType<JSXElementInput> =
  BaseJSXElementInput.extend({
    children: z
      .array(
        z.union([
          z.string(),
          JSXTextElementInput,
          z.lazy(() => JSXElementInput),
        ]),
      )
      .optional(),
  }).strict();
export type JSXElementInput = BaseJSXElementInput & {
  children?: Array<JSXElementInput | JSXTextElementInput | string>;
};
